import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { CSSProperties, memo } from 'react'
import { notesAddUser } from '../../../actions/notes'
import { AppStateType, useAppStateSelector } from '../../Hooks/useAppState'
import { useIsomorphicLayoutEffect } from '../../Hooks/useIsomorphicLayoutEffect'
import { useStatusMessage } from '../../Hooks/useStatusMessage'
import { useUserSelector } from '../../Hooks/useUser'
import { getThemeSpacing } from '../../styles/appTheme'
import { palette } from '../../styles/colors'
import shape from '../../styles/shape'
import NotesPanel from './NotesPanel'

const notesContainerStyles = css({
    display: 'flex',
    position: 'relative',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    border: `1px solid ${palette.buttonBorder}`,
    borderRadius: shape.borderRadius,
    height: '100%',
})

const NotesLabel = styled('div')({
    position: 'absolute',
    fontSize: 18,
    left: getThemeSpacing(1.25),
    top: getThemeSpacing(1.25),
})

interface EmbeddedNotesProps {
    userId: number
    style?: CSSProperties
    ref?: any
    isDrawer?: boolean
}

const EmbeddedNotes = memo((props: EmbeddedNotesProps) => {
    const { userId: id, style, isDrawer, ref } = props
    const name = useUserSelector(id, 'name')
    const notes = useUserSelector(id, 'notes')
    const lastLogin = useUserSelector(id, 'lastLogin')

    const token = useAppStateSelector(AppStateType.SESSION, 'token')
    const { statusMessage, showStatus, onStatusMessage } = useStatusMessage()

    useIsomorphicLayoutEffect(() => {
        if (!id) return
        // we haven't received a response from the `userNotepad` API
        if (notes === undefined) return
        // no notes for this user. we can't use notes === null
        // because we set notes to null when deleting the note
        if (lastLogin === null) {
            notesAddUser(token, name)
        }
    }, [id, lastLogin, notes])

    if (!id) return null

    return (
        <div
            css={{
                ...notesContainerStyles,
                ...(isDrawer && { marginTop: getThemeSpacing(2) }),
            }}
        >
            <NotesLabel>Notes</NotesLabel>
            <NotesPanel
                ref={ref}
                userId={id}
                showStatus={showStatus}
                statusMessage={statusMessage}
                onStatusMessage={onStatusMessage}
                embedded
                textareaStyle={style}
            />
        </div>
    )
})

export default EmbeddedNotes
